import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SolutionDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [markdownContent, setMarkdownContent] = useState<string>('');

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchMarkdown = async () => {
            try {
                const response = await fetch(`/data/solutions/${id}.md`);
                if (response.ok) {
                    const text = await response.text();
                    setMarkdownContent(text);
                } else {
                    console.error(`Failed to load markdown for ${id}`);
                    setMarkdownContent('Description not available.');
                }
            } catch (error) {
                console.error(`Error loading markdown for ${id}:`, error);
                setMarkdownContent('Error loading description.');
            }
        };

        if (id) {
            fetchMarkdown();
        }
    }, [id]);

    // Split content to remove "<!-- MORE -->" delimiter and show only the long text
    const contentParts = markdownContent.split('<!-- MORE -->');
    // If the delimiter exists, show the second part (long text). Otherwise show everything (fallback).
    const displayContent = contentParts.length > 1 ? contentParts[1] : contentParts[0];

    return (
        <div className="d-flex flex-column min-vh-100">
            <Header />

            <main className="flex-grow-1" style={{ position: 'relative' }}>
                <div className="container solution-detail-inner">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {/* Close Button */}
                            <div className="d-flex justify-content-end mb-4">
                                <button
                                    onClick={() => navigate('/solutions')}
                                    style={{
                                        background: 'rgba(24, 195, 186, 0.1)',
                                        border: '1px solid rgba(24, 195, 186, 0.3)',
                                        borderRadius: '50px',
                                        color: '#18C3BA',
                                        padding: '0.5rem 1.5rem',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    className="close-btn"
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(24, 195, 186, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(24, 195, 186, 0.1)';
                                    }}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    Close
                                </button>
                            </div>

                            {/* Content */}
                            <div className="solution-detail-content" style={{ color: '#D5D6CC' }}>
                                <ReactMarkdown
                                    components={{
                                        h1: ({ node, children, ...props }) => <h1 style={{
                                            fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                                            fontWeight: '700',
                                            color: '#EFEFE3',
                                            marginBottom: '2rem',
                                            lineHeight: '1.2'
                                        }} {...props}>{children}</h1>,
                                        h2: ({ node, children, ...props }) => <h2 style={{
                                            fontSize: '1.8rem',
                                            color: '#EFEFE3',
                                            marginTop: '2.5rem',
                                            marginBottom: '1.5rem',
                                            fontWeight: '600'
                                        }} {...props}>{children}</h2>,
                                        h3: ({ node, children, ...props }) => <h3 style={{
                                            fontSize: '1.4rem',
                                            color: '#EFEFE3',
                                            marginTop: '2rem',
                                            marginBottom: '1rem',
                                            fontWeight: '600'
                                        }} {...props}>{children}</h3>,
                                        p: ({ node, ...props }) => <p style={{
                                            fontSize: '1.1rem',
                                            lineHeight: '1.8',
                                            marginBottom: '1.5rem'
                                        }} {...props} />,
                                        ul: ({ node, ...props }) => <ul style={{
                                            paddingLeft: '1.5rem',
                                            marginBottom: '1.5rem'
                                        }} {...props} />,
                                        li: ({ node, ...props }) => <li style={{
                                            marginBottom: '0.8rem',
                                            fontSize: '1.1rem',
                                            lineHeight: '1.6'
                                        }} {...props} />
                                    }}
                                >
                                    {displayContent}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default SolutionDetailPage;
